import { ReactNode } from 'react'
import { Dialog, DialogHeader, DialogContent, DialogFooter } from './Dialog'
import { Button } from './button'
import {
  Warning,
  Trash,
  XCircle,
  CheckCircle,
  Question,
  Spinner,
} from '@phosphor-icons/react'

type ConfirmVariant = 'danger' | 'warning' | 'info' | 'success'

interface ConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void | Promise<void>
  title: string
  message: string | ReactNode
  confirmText?: string
  cancelText?: string
  variant?: ConfirmVariant
  isLoading?: boolean
  icon?: ReactNode
}

const variantConfig = {
  danger: {
    icon: <Trash size={32} weight="duotone" />,
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    buttonColor: 'bg-red-600 hover:bg-red-700',
  },
  warning: {
    icon: <Warning size={32} weight="duotone" />,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    buttonColor: 'bg-amber-600 hover:bg-amber-700',
  },
  info: {
    icon: <Question size={32} weight="duotone" />,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    buttonColor: 'bg-blue-600 hover:bg-blue-700',
  },
  success: {
    icon: <CheckCircle size={32} weight="duotone" />,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    buttonColor: 'bg-emerald-600 hover:bg-emerald-700',
  },
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'warning',
  isLoading = false,
  icon,
}: ConfirmDialogProps) {
  const config = variantConfig[variant]

  const handleConfirm = async () => {
    await onConfirm()
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="sm"
      closeOnOverlayClick={!isLoading}
      showCloseButton={!isLoading}
    >
      <DialogHeader>
        <div className="flex flex-col items-center text-center">
          <div className={`w-16 h-16 rounded-full ${config.iconBg} ${config.iconColor} flex items-center justify-center mb-4`}>
            {icon || config.icon}
          </div>
          <h3 className="text-xl font-semibold text-gray-900">
            {title}
          </h3>
        </div>
      </DialogHeader>

      <DialogContent>
        <div className="text-center text-gray-600">
          {typeof message === 'string' ? <p>{message}</p> : message}
        </div>
      </DialogContent>

      <DialogFooter className="justify-center">
        <Button
          variant="outline"
          onClick={onClose}
          disabled={isLoading}
        >
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={isLoading}
          className={`text-white ${config.buttonColor}`}
        >
          {isLoading ? (
            <>
              <Spinner size={20} className="animate-spin mr-2" />
              Aguarde...
            </>
          ) : (
            confirmText
          )}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

// Convenience components for common use cases

interface DeleteConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void | Promise<void>
  itemName?: string
  isLoading?: boolean
}

export function DeleteConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  itemName,
  isLoading,
}: DeleteConfirmDialogProps) {
  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title="Confirmar Exclusao"
      message={
        itemName
          ? `Tem certeza que deseja excluir "${itemName}"? Esta acao nao pode ser desfeita.`
          : 'Tem certeza que deseja excluir este item? Esta acao nao pode ser desfeita.'
      }
      confirmText="Excluir"
      cancelText="Cancelar"
      variant="danger"
      isLoading={isLoading}
    />
  )
}

interface CancelConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void | Promise<void>
  title?: string
  message?: string
  isLoading?: boolean
}

export function CancelConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title = 'Cancelar Cotacao',
  message = 'Tem certeza que deseja cancelar esta cotacao? Os agentes de carga nao poderao mais enviar propostas.',
  isLoading,
}: CancelConfirmDialogProps) {
  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title={title}
      message={message}
      confirmText="Sim, Cancelar"
      cancelText="Voltar"
      variant="warning"
      isLoading={isLoading}
      icon={<XCircle size={32} weight="duotone" />}
    />
  )
}
